package nl.volleyplanner.web.controller.planner

import kotlinx.browser.document
import kotlinx.coroutines.flow.*
import kotlinx.datetime.format
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.planner.PlannerPrerequisites
import nl.volleyplanner.api.planner.PlanningProblem
import nl.volleyplanner.util.SeasonUtil
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.controller.planner.planner_configurations.PlannerConfigurationController
import nl.volleyplanner.web.controller.planner.rulesets_config_v2.PlannerTeamPouleConfigControllerV2
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.LocalDateFormatters
import nl.volleyplanner.web.util.LocalDateTimeFormatters
import org.w3c.dom.HTMLElement

fun buildParameterizedPath(pathTemplate: String, parameters: Map<String, String>): String {
    return pathTemplate.replace(Regex(":(\\w+)")) { matchResult ->
        val key = matchResult.groupValues[1]
        parameters[key] ?: matchResult.value // Keep the placeholder if no value is found
    }
}

abstract class ConfigCard(
    private val path: String,
) : HTMLComponent() {
    abstract val pathParameters: Map<String, String>
    abstract val isActive: Boolean
    abstract val label: String
    open val isOptional: Boolean = false

    open fun FlowContent.renderDescriptionContent() {}

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "vp-index-config-card") {
            div (classes = "p-4 text-center border shadow-sm position-relative h-100") {
                if (isActive) {
                    classes += "bg-white"
                } else {
                    classes += "bg-secondary-subtle"
                }
                val pageHref = if (pathParameters.isEmpty()) {
                    path
                } else {
                    buildParameterizedPath(path, pathParameters)
                }
                a(
                    classes = "text-decoration-none stretched-link"
                ) {
                    if (isActive) {
                        href = "/planner${pageHref}"
                        classes += "text-body"
                    } else {
                        classes += "text-secondary"
                    }
                    h5(classes = "card-title text-decoration-underline") {
                        if (isActive) {
                            classes += "text-primary-emphasis"
                        } else {
                            classes += "text-secondary-emphasis"
                        }
                        if (isOptional) {
                            classes += "mb-0"
                        } else {
                            classes += "mb-3"
                        }
                        +label
                    }
                    if (isOptional) {
                        span (classes = "d-block small text-secondary fst-italic mb-2") { +"Optioneel" }
                    }
                    renderDescriptionContent()
                }
            }
        }
    }
}

class StaticConfigCard(
    val controller: WebPageController,
    override val label: String
) : ConfigCard(controller.path) {
    override val isActive = true
    override val pathParameters = emptyMap<String, String>()
    override suspend fun getState() = null
}

class FakeConfigCard(
    override val label: String,
    private val descriptionBlock: FlowContent.() -> Unit,
) : ConfigCard("") {
    override val isActive = false
    override val pathParameters = emptyMap<String, String>()
    override suspend fun getState() = null

    override fun FlowContent.renderDescriptionContent() {
        descriptionBlock()
    }
}

class TasksCard(
    val plannerConstraintConfigurationController: PlannerTeamPouleConfigControllerV2,
    val prerequisitesFlow: StateFlow<PlannerPrerequisites?>
) : ConfigCard(plannerConstraintConfigurationController.path) {
    override val label = "Taken"
    override val isActive: Boolean
        get() = prerequisitesFlow.value != null && prerequisitesFlow.value!!.seasonPrequisites.values.any { it.refereeGroupCount > 0 }
    override val pathParameters: Map<String, String> = emptyMap()
    override suspend fun getState() = prerequisitesFlow

    override fun FlowContent.renderDescriptionContent() {
        if (prerequisitesFlow.value == null) { spinner () } else {
            if (prerequisitesFlow.value!!.seasonPrequisites.values.all { it.refereeGroupCount == 0L }) {
                span(classes = "small") {
                    +"Configureer scheidsrechters eerst"
                }
            }
        }
    }
}

class PlannerConstraintConfigCard(
    val plannerConfigurationController: PlannerConfigurationController,
    val prerequisitesFlow: StateFlow<PlannerPrerequisites?>
) : ConfigCard(plannerConfigurationController.path) {
    override val label = "Configuratie"
    override val isActive = true
    override val pathParameters: Map<String, String> = emptyMap()
    override suspend fun getState() = prerequisitesFlow
}

class ProblemEditorCard(
    val problemEditorController: ProblemEditorController,
    val problem: PlanningProblem,
    val prerequisitesFlow: StateFlow<PlannerPrerequisites?>
) : ConfigCard(problemEditorController.path) {
    private val seasonName = SeasonUtil.getSeasonName(problem.startDate)

    override val label = problem.name
    override val isActive: Boolean
        get() = prerequisitesFlow.value != null && prerequisitesFlow.value!!.memberCount > 0
                && (prerequisitesFlow.value!!.seasonPrequisites[seasonName]?.refereeGroupCount ?: 0) > 0
                && (prerequisitesFlow.value!!.seasonPrequisites[seasonName]?.nevoboTeamCount ?: 0) > 0
    override val pathParameters: Map<String, String> = mapOf("problem_slug" to problem.slug)
    override suspend fun getState() = prerequisitesFlow

    override fun FlowContent.renderDescriptionContent() {
        div (classes = "") {
            +"${problem.startDate.format(LocalDateFormatters.DD_MM_YYYY_NUMBERS)} t/m ${problem.endDate.format(LocalDateFormatters.DD_MM_YYYY_NUMBERS)}"
        }
        div {
            if (prerequisitesFlow.value == null) {
                spinner()
            } else {
                if (!isActive) {
                    val seasonPrerequisites = prerequisitesFlow.value!!.seasonPrequisites[seasonName]
                    ul(classes = "list-unstyled ") {
                        if ((seasonPrerequisites?.refereeGroupCount ?: 0) == 0L) {
                            li { +"Scheidsrechter configuratie verplicht" }
                        }
                        if ((seasonPrerequisites?.nevoboTeamCount ?: 0) == 0L) {
                            li { +"Wachten op Nevobo data" }
                        }
                        if (prerequisitesFlow.value!!.memberCount == 0L) {
                            li { +"Eerst leden importeren" }
                        }
                    }
                }
            }
        }
    }
}

class NevoboImportCard(
    val nevoboImportController: NevoboImportController,
    val prerequisitesFlow: StateFlow<PlannerPrerequisites?>
) : ConfigCard(nevoboImportController.path) {
    override val label = "Wedstrijden"
    override val isActive = true
    override val pathParameters: Map<String, String> = emptyMap()
    override suspend fun getState() = prerequisitesFlow

    override fun FlowContent.renderDescriptionContent() {
        if (prerequisitesFlow.value == null) { spinner() } else {
            ul (classes = "list-unstyled ") {
                li {
                    val lastSeason = prerequisitesFlow.value!!.seasonPrequisites.keys.maxOf { it }
                    strong { +"${prerequisitesFlow.value!!.seasonPrequisites.getValue(lastSeason).nevoboTeamCount}" }
                    +" teams in systeem"
                }
                li {
                    icon("bi-arrow-repeat")
                    if (prerequisitesFlow.value!!.nevoboSyncInProgress) {
                        +"Sync bezig..."
                    } else if (prerequisitesFlow.value!!.lastNevoboSyncDateTime != null) {
                        +"Laatste sync: ${prerequisitesFlow.value!!.lastNevoboSyncDateTime!!.format(LocalDateTimeFormatters.DAY_MONTH_YEAR_TIME_TEXT_SHORT)}"
                    } else {
                        +"Nog geen sync gestart"
                    }
                }
            }
        }
    }
}

class MemberImportCard(
    val memberImportController: MemberImportController,
    val prerequisitesFlow: StateFlow<PlannerPrerequisites?>
) : ConfigCard(memberImportController.path) {
    override val label = "Leden"
    override val isActive: Boolean
        get() = prerequisitesFlow.value != null && prerequisitesFlow.value!!.seasonPrequisites.any { it.value.refereeGroupCount > 0 }
    override val pathParameters: Map<String, String> = emptyMap()
    override suspend fun getState() = prerequisitesFlow

    override fun FlowContent.renderDescriptionContent() {
        if (prerequisitesFlow.value == null) { spinner() } else {
            if (!isActive) {
                ul (classes = "list-unstyled ") {
                    if (prerequisitesFlow.value!!.seasonPrequisites.values.all { it.refereeGroupCount == 0L }) {
                        li { +"Configureer scheidsrechters eerst" }
                    }
                }
            } else {
                ul (classes = "list-unstyled ") {
                    li {
                        strong { +"${prerequisitesFlow.value!!.memberCount}" }
                        +" leden in systeem"
                    }
                    li {
                        icon("bi-arrow-repeat")
                        if (prerequisitesFlow.value!!.lastMemberUpload != null) {
                            +"Laatste upload: ${prerequisitesFlow.value!!.lastMemberUpload!!.format(LocalDateTimeFormatters.DAY_MONTH_YEAR_TIME_TEXT_SHORT)}"
                        } else {
                            +"Nog geen leden geupload"
                        }
                    }
                }
            }
        }
    }
}

class VolunteerImportCard(
    val volunteerImportController: VolunteerImportController,
    val prerequisitesFlow: StateFlow<PlannerPrerequisites?>
) : ConfigCard(volunteerImportController.path) {
    override val label = "Vrijwilligers"
    override val isOptional = true
    override val isActive: Boolean
        get() = prerequisitesFlow.value != null && prerequisitesFlow.value!!.memberCount > 0
    override val pathParameters: Map<String, String> = emptyMap()
    override suspend fun getState() = prerequisitesFlow

    override fun FlowContent.renderDescriptionContent() {
        if (prerequisitesFlow.value == null) { spinner () } else {
            if (!isActive) {
                ul (classes = "list-unstyled") {
                    if (prerequisitesFlow.value!!.memberCount == 0L) {
                        li { +"Eerst leden importeren" }
                    }
                }
            } else {
                span {
                    val lastSeason = prerequisitesFlow.value!!.seasonPrequisites.keys.maxOf { it }
                    strong { +"${prerequisitesFlow.value!!.seasonPrequisites.getValue(lastSeason).volunteerCount}" }
                    +" vrijwilligerstaken in systeem"
                }
            }
        }
    }
}

class PlanningProblemsComponent(
    private val problemEditorController: ProblemEditorController,
    val prerequisitesFlow: StateFlow<PlannerPrerequisites?>
) : HTMLComponent() {
    val dataFlow = MutableStateFlow<List<PlanningProblem>?>(null)

    fun refreshData() {
        if (WebUserAndOrganisationService.selectedOrganisationState.value == null) return
        launchAsync {
            WebUserAndOrganisationService.selectedOrganisationState.value!!.volleyplannerApiClient
                .organisation.plannerConfiguration.listProblems(false)
        }.onComplete { data -> dataFlow.value = data }
    }

    override fun onReady() {
        super.onStateChange()
        refreshData()
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "border align-items-stretch mt-4 rounded-top-1") {
            div(classes = "p-2 text-bg-primary bg-gradient d-flex align-items-center flex-wrap") {
                h4(classes = "m-0 fw-semibold") { +"Planning Maken" }
            }
            div(classes = "p-3 bg-body-secondary border-top border-bottom") {
                unsafe { +"Als alles is ingesteld kan je een planning laten genereren. Selecteer een tijdsvak en ga aan de slag!" }
            }
            div(classes = "d-flex flex-wrap bg-light") {
                if (dataFlow.value == null) {
                    spinner()
                } else {
                    dataFlow.value!!
                        .sortedByDescending { it.startDate }
                        .chunked(2)
                        .flatMap { it.reversed() }
                        .forEach { problem ->
                            div (classes = "w-50 mb-2") {
                                component(ProblemEditorCard(problemEditorController, problem, prerequisitesFlow))
                            }
                        }
                }
            }
        }
    }

    override suspend fun getState() = dataFlow
}


class PlannerIndexComponent(
    val gettingStartedController: GettingStartedController,
    val plannerRulesetsConfigurationController: PlannerTeamPouleConfigControllerV2,
    val plannerConfigurationController: PlannerConfigurationController,
    val nevoboImportController: NevoboImportController,
    val memberImportController: MemberImportController,
    val volunteerImportController: VolunteerImportController,
    val problemEditorController: ProblemEditorController
) : HTMLComponent() {

    // Taken: prerequisite refereegroups & wedstrijddata klaar
    // Leden: Eerste wedstrijddata sync klaar
    // Vrijwilligers: > 0 leden
    // Planning: scheidsrechtercodes, taken en leden + Eerste wedstrijddata sync klaar
    private val plannerPrerequisitesFlow = WebUserAndOrganisationService.selectedOrganisationState
        .map { organisationContext ->
            organisationContext?.volleyplannerApiClient?.organisation?.plannerConfiguration?.getPlannerPrerequisites()
        }.stateIn(CoroutineManager.RENDER_SCOPE, SharingStarted.Eagerly, null)

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        if (plannerPrerequisitesFlow.value == null) {
            return consumer.div {
                spinner()
            }
        }
        return consumer.div {
            div (classes = "row row-cols-2 row-cols-md-3") {
                component(StaticConfigCard(gettingStartedController, "Handleiding: snel aan de slag met je eerste planning!"))
            }
            div(classes = "border align-items-stretch mt-4 rounded-top-1") {
                div(classes = "p-2 text-bg-primary bg-gradient d-flex align-items-center flex-wrap") {
                    h4(classes = "m-0 fw-semibold") { +"Gegevens" }
                }
                div(classes = "p-3 bg-body-secondary border-top border-bottom") {
                    unsafe { +"Voor een planning moeten we minimaal weten welke <strong>leden</strong> in welk team zitten en wie daarvan op welk niveau mag fluiten." }
                    br {}
                    unsafe { +"<strong>Wedstrijden</strong> worden automatisch opgehaald bij de Nevobo. Hier kan je de laatste wijzigingen nog laten ophalen." }
                    br {}
                    unsafe { +"Optioneel kan je <strong>vrijwilligers</strong> doorgeven om deze leden minder vaak of helemaal niet in te plannen." }
                }
                div (classes = "d-flex gx-2 bg-light flex-wrap") {
                    component(NevoboImportCard(nevoboImportController, plannerPrerequisitesFlow))
                    component(MemberImportCard(memberImportController, plannerPrerequisitesFlow))
                    component(VolunteerImportCard(volunteerImportController, plannerPrerequisitesFlow))
                }
            }

            div(classes = "border align-items-stretch mt-4 rounded-top-1") {
                div(classes = "p-2 text-bg-primary bg-gradient d-flex align-items-center flex-wrap") {
                    h4(classes = "m-0 fw-semibold") { +"Planner Configuratie" }
                }
                div(classes = "p-3 bg-body-secondary border-top border-bottom") {
                    unsafe { +"Stel bij <strong>Taken</strong> in wie op welk niveau mag fluiten/tellen en welk niveau bij welk team hoort" }
                    br {}
                    unsafe { +"Bij <strong>Configuratie</strong> stel je algemene regels voor de planner in. Zoals extra limieten voor de jeugd." }
                }
                div (classes = "d-flex gx-2 bg-light") {
                    component(TasksCard(plannerRulesetsConfigurationController, plannerPrerequisitesFlow))
                    component(PlannerConstraintConfigCard(plannerConfigurationController, plannerPrerequisitesFlow))
                }
            }

            component(PlanningProblemsComponent(problemEditorController, plannerPrerequisitesFlow))
        }
    }

    override suspend fun getState() = plannerPrerequisitesFlow
}

class PlannerIndexController : RootPageController() {
    override val name = "PlannerIndexController"
    override val title = "Planner"
    override val path = "/planner"
    override val requiredAuthLevel = AuthLevel.AdminUser

    val gettingStartedController = GettingStartedController(this)
    //val plannerRulesetsConfigurationController = PlannerRulesetsConfigurationController(this)
    val plannerRulesetsConfigurationController = PlannerTeamPouleConfigControllerV2(this)
    val plannerConfigurationController = PlannerConfigurationController(this)
    val nevoboImportController = NevoboImportController(this)
    val memberImportController = MemberImportController(this)
    val volunteerImportController = VolunteerImportController(this)
    val problemEditorController = ProblemEditorController(this)

    override val childPages = listOf(
        gettingStartedController,
        plannerRulesetsConfigurationController,
        plannerConfigurationController,
        nevoboImportController,
        memberImportController,
        volunteerImportController,
        problemEditorController
    )

    override fun createRuntimeInstance() = PlannerIndexController()

    override suspend fun renderContent(): HTMLElement {
        return ComponentManager.createComponent(PlannerIndexComponent(
            gettingStartedController,
            plannerRulesetsConfigurationController,
            plannerConfigurationController,
            nevoboImportController,
            memberImportController,
            volunteerImportController,
            problemEditorController
        ), document.create)
    }
}